import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { Button, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowClockwise16Regular } from "@fluentui/react-icons";

interface NewformaButtonProps extends LocalizeContextProps {
    disabled?: boolean;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    children: string | JSX.Element;
    style?: React.CSSProperties;
    testid?: string;
    icon?: JSX.Element;
    className?: string;
}

const buttonStyles = makeStyles({
    buttonPrimary: {
        fontSize: "14px",
        height: "32px",
    },
    buttonOutline: {
        fontSize: "14px",
        height: "32px",
    },
    buttonSubtle: {
        color: tokens.colorBrandForeground1,
        fontSize: "14px",
        height: "32px",
        "&:hover": {
            color: tokens.colorCompoundBrandForeground1Hover,
            textDecoration: "underline",
        },
    },
    buttonDanger: {
        backgroundColor: tokens.colorPaletteRedBackground3,
        color: tokens.colorNeutralForegroundOnBrand,
        fontSize: "14px",
        height: "32px",
        "&:hover": {
            backgroundColor: tokens.colorPaletteRedBackground2,
            color: tokens.colorNeutralForegroundOnBrand,
        },
    },
    footerButton: {
        height: "32px",
        flexGrow: "1",
        padding: "0px",
        borderRadius: "0px",
        fontSize: "14px",
    },
    reloadButton: {
        height: "20px",
        width: "fit-content",
        color: tokens.colorBrandForeground1,
        "&:hover": {
            color: tokens.colorCompoundBrandForeground1Hover,
        },
    },
});

export const NewformaButton = withLocalize(
    ({
        children,
        onClick,
        disabled,
        appearance,
        style,
        testid,
        icon,
        className: classNameProp,
    }: NewformaButtonProps & { appearance?: "primary" | "outline" | "subtle" | "danger" }) => {
        const styles = buttonStyles();
        let className = styles.buttonPrimary;
        let fluentAppearance: "primary" | "outline" | "subtle" = "primary";
        if (appearance === "outline") {
            className = styles.buttonOutline;
            fluentAppearance = "outline";
        } else if (appearance === "subtle") {
            className = styles.buttonSubtle;
            fluentAppearance = "subtle";
        } else if (appearance === "danger") {
            className = styles.buttonDanger;
            fluentAppearance = "primary";
        }
        return (
            <Button
                data-testid={testid ?? ""}
                style={style}
                className={mergeClasses(className, classNameProp)}
                as="button"
                appearance={fluentAppearance}
                size="small"
                disabledFocusable={false}
                onClick={onClick}
                disabled={disabled}
                icon={icon}
            >
                {children}
            </Button>
        );
    }
);

/** Blue button used for the footer. */
export const NewformaButtonFooter = withLocalize(({ children, onClick, disabled, testid }: NewformaButtonProps) => {
    const styles = buttonStyles();
    return (
        <Button
            data-testid={testid ?? ""}
            className={styles.footerButton}
            as="button"
            appearance="primary"
            size="large"
            disabledFocusable={false}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </Button>
    );
});

/** Reload Projects button and other places */
export const NewformaButtonReload = withLocalize(({ children, onClick, disabled, testid }: NewformaButtonProps) => {
    const styles = buttonStyles();
    return (
        <Button
            data-testid={testid ?? ""}
            className={styles.reloadButton}
            icon={<ArrowClockwise16Regular />}
            iconPosition="before"
            as="button"
            appearance="transparent"
            size="small"
            disabledFocusable={false}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </Button>
    );
});
